// Sidebar.jsx — PORTAL Trial EDC — Light Theme, CRF v1.22 + Form K & L
import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  ClipboardCheck, Baby, UserRoundCheck, CalendarDays, Hospital,
  FileHeart, HeartPulse, Microscope, TestTube2,
  Activity, Eye, Stethoscope, TrendingUp, BarChart3,
  Building2, Cpu, ShieldAlert, ClipboardList, FileText,
  LayoutDashboard, LogOut, Check, Lock, ChevronRight, Menu, X, Search,
} from 'lucide-react';
import { useFormProgress } from './context/FormProgressContext';
import { useActiveFormSessionRegistry } from './context/ActiveFormSessionContext';
import { useAuth } from './context/AuthContext';
import api from './api/axios';
import { isUsableEnrollmentId, pageEnrollmentId, pageScreeningId } from './utils/enrollmentId';
import { realCalendarDateYmd, NICU_DAY_GRACE_HOUR } from './utils/datetime';
import './Sidebar.css';

/* All forms unlock after A+B are done */
const PREREQS = {
  form_a:               [],
  form_b:               ['form_a'],
  form_c:               ['form_a', 'form_b'],
  form_d:               ['form_a', 'form_b'],
  form_e:               ['form_a', 'form_b'],
  fio2_auc:             ['form_a', 'form_b'],
  vs6_1:                ['form_a', 'form_b'],
  infect_gi_hema:       ['form_a', 'form_b'],
  metab_renal_vasc_eye: ['form_a', 'form_b'],
  minimal_monitoring:   ['form_a', 'form_b'],
  form_f:               ['form_a', 'form_b'],
  form_g:               ['form_a', 'form_b'],
  form_h:               ['form_a', 'form_b'],
  form_i:               ['form_a', 'form_b'],
  form_j:               ['form_a', 'form_b'],
  form_k:               ['form_a', 'form_b'],
  form_l:               ['form_a', 'form_b'],
  form_y_sae:           ['form_a', 'form_b'],
  adverse_events:       ['form_a', 'form_b'],
  sae_list:             ['form_a', 'form_b'],
};

/* CRF v1.22 exact order */
const SECTIONS = [
  {
    key: 'core',
    title: 'Core Forms',
    items: [
      { id: 'form_a', label: 'Form A', sub: 'Screening',             path: '/form-a', Icon: ClipboardCheck },
      { id: 'form_b', label: 'Form B', sub: 'Birth & Resuscitation', path: '/form-b', Icon: Baby           },
      { id: 'form_c', label: 'Form C', sub: 'Maternal Details',      path: '/form-c', Icon: UserRoundCheck },
      { id: 'form_d', label: 'Form D', sub: 'Day 1 Postnatal Life',  path: '/form-d', Icon: CalendarDays   },
      { id: 'form_e', label: 'Form E', sub: 'NICU Admission',        path: '/form-e', Icon: Hospital       },
    ],
  },
  {
    key: 'helpers',
    title: 'Monitoring Logs',
    items: [
      { id: 'minimal_monitoring',   label: 'Daily Monitoring Sheet (DMS)', sub: 'Minimal Monitoring',  path: '/minimal-monitoring',      Icon: Activity   },
      { id: 'vs6_1',                label: 'Helper 2', sub: 'Resp / CV / Neuro',   path: '/vs6-1',                   Icon: HeartPulse },
      { id: 'fio2_auc',             label: 'Helper 3', sub: 'FiO₂ Logging',        path: '/fio2-auc',                Icon: FileHeart  },
      { id: 'infect_gi_hema',       label: 'Helper 4', sub: 'Infect / GI / Hema',  path: '/infect-gi-hema-log',      Icon: Microscope },
      { id: 'metab_renal_vasc_eye', label: 'Helper 5', sub: 'Metab / Renal / Eye', path: '/metab-renal-vasc-eye-log',Icon: TestTube2  },
    ],
  },
  {
    key: 'assessment',
    title: 'Clinical Assessment',
    items: [
      { id: 'form_f', label: 'Form F', sub: 'Cranial Ultrasonography', path: '/form-f', Icon: Activity    },
      { id: 'form_g', label: 'Form G', sub: 'ROP Screening Record',    path: '/form-g', Icon: Eye         },
      { id: 'form_h', label: 'Form H', sub: 'Neonatal Morbidities',    path: '/form-h', Icon: Stethoscope },
    ],
  },
  {
    key: 'outcomes',
    title: 'Outcome Forms',
    items: [
      { id: 'form_i', label: 'Form I', sub: 'Study Outcome Assessment',   path: '/form-i', Icon: TrendingUp },
      { id: 'form_j', label: 'Form J', sub: 'External Hospital Outcomes', path: '/form-j', Icon: Building2  },
      { id: 'form_k', label: 'Form K', sub: 'MRI Brain Assessment',       path: '/form-k', Icon: Cpu        },
      { id: 'form_l', label: 'Form L', sub: 'Blender Data & Summary',     path: '/form-l', Icon: BarChart3  },
    ],
  },
  {
    key: 'safety',
    title: 'Safety',
    items: [
      { id: 'adverse_events', label: 'AE',     sub: 'Adverse Events', path: '/adverse-events', Icon: ClipboardList },
      { id: 'form_y_sae',     label: 'Form Y', sub: 'SAE Reporting',  path: '/form-y-sae',     Icon: ShieldAlert   },
      { id: 'sae_list',       label: 'SAE',     sub: 'SAE Listing',    path: '/sae-list',       Icon: FileText      },
    ],
  },
];

// The DMS is a same-day scratchpad feeding Helpers 2-5, with nothing
// mandatory — it gets no green tick and isn't counted (PI decision
// 2026-09-26). Its sidebar row shows a neutral "Today: logged / not yet
// logged" note instead; completeness is judged in the helper forms.
const NO_TICK_ITEMS = new Set(['minimal_monitoring']);
const TOTAL_FORMS = SECTIONS.reduce((n, s) => n + s.items.filter(i => !NO_TICK_ITEMS.has(i.id)).length, 0);
const DMS_META_KEYS = new Set(['id', 'date', 'time', 'slot_time']);
const sheetHasReading = (sheet) => {
  let parsed = sheet?.entries_json;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { parsed = null; }
  }
  if (!parsed || typeof parsed !== 'object') return false;
  return Object.values(parsed).some(list => Array.isArray(list) && list.some(e =>
    e && Object.entries(e).some(([k, v]) =>
      !DMS_META_KEYS.has(k) && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))));
};
const ALL_ITEMS = SECTIONS.flatMap(s => s.items);
const getCurrentFormMeta = (id) => ALL_ITEMS.find(i => i.id === id);
const ROLE_LABELS = {
  superadmin:'Super Admin', global_scientist:'Global Scientist', admin:'Admin', pi:'Principal Investigator',
  scientist:'Scientist', nurse:'Research Nurse', deo:'Data Entry Operator', monitor:'Monitor',
};
const consentAllowsEnrollment = value => value === 'Yes'; // "Trial run" retired 2026-10-18

export default function Sidebar({ currentForm }) {
  const { completedForms = [], unlockedForms = [], isProgressLoaded, fetchProgress } = useFormProgress();
  // Unlocking follows "has a record" (unlockedForms); the tick follows
  // "complete" (completedForms). A complete form is always unlocked too.
  const isUnlockedPrereq = (p) => unlockedForms.includes(p) || completedForms.includes(p);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { getActiveSession } = useActiveFormSessionRegistry() || {};

  // ≤1024px: sticky bar + overlay drawer (does not push form content)
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = () => setNavOpen(false);
  const toggleNav = () => setNavOpen(o => !o);

  const guardedNavigate = async (e, to) => {
    e?.preventDefault?.();
    const session = getActiveSession?.() || null;
    if (session?.isDirty?.()) {
      try {
        await session.doSave();
      } catch (err) {
        const proceed = window.confirm(
          "This form couldn't be saved (you may be offline). Leave anyway and " +
          "lose unsaved changes, or stay and try again?\n\nOK = leave anyway, " +
          "Cancel = stay on this page."
        );
        if (!proceed) return;
      }
    }
    closeNav();
    navigate(to);
  };

  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [navOpen]);

  // A direct / bookmarked link names the baby in the URL (/vs6-1/01-D-901).
  // After a fresh login the stored id is gone, which left the sidebar at 0/N
  // for a baby the page itself was showing (found 2026-09-26). The URL wins;
  // if it names a different baby, drop the old baby's screening id and lock
  // flags so they can't leak across.
  const syncIdFromUrl = () => {
    const seg = decodeURIComponent(window.location.pathname.split('/')[2] || '');
    if (!isUsableEnrollmentId(seg)) return;
    if (localStorage.getItem('current_enrollment_id') === seg) return;
    localStorage.setItem('current_enrollment_id', seg);
    localStorage.removeItem('current_screening_id');
    localStorage.removeItem('enrollment_locked');
    localStorage.removeItem('enrollment_lock_reason');
  };
  const readIds = () => {
    syncIdFromUrl();
    return readStoredIds();
  };
  // This tab's baby (utils/enrollmentId.js): the address first, so another
  // tab's write to the shared ids never changes what this sidebar shows.
  // Ignores typing stubs like "01-" left by Form B focus/autosave.
  const readStoredIds = () => ({
    screeningId:  pageScreeningId(),
    enrollmentId: pageEnrollmentId(),
  });
  const [ids, setIds] = useState(readIds);
  const screeningId = ids.screeningId;
  const enrollmentId = ids.enrollmentId;

  // Another tab's write (a storage event with a key) is only read, never
  // answered with a write: writing this tab's baby back made two tabs on
  // different babies flip the shared id without end (2026-11-03). Focusing
  // this tab, or a same-tab nudge (no key), still claims the shared id.
  useEffect(() => {
    const onStorage = (e) => setIds(e && e.key ? readStoredIds() : readIds());
    const onFocus = () => setIds(readIds());
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    return () => { window.removeEventListener('storage', onStorage); window.removeEventListener('focus', onFocus); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- readIds only reads storage and the URL

  // Route changes (e.g. View Entries → Form A) update localStorage in the same
  // tab; re-read IDs so progress fetch runs for the newly opened patient.
  useEffect(() => {
    setIds(readIds());
  }, [location.pathname]);

  useEffect(() => {
    if (enrollmentId && enrollmentId !== 'undefined' && enrollmentId !== 'null') {
      fetchProgress(enrollmentId);
    }
  }, [enrollmentId, location.pathname]); // eslint-disable-line

  // DMS "Today" note: does the current NICU working day's sheet have at least
  // one reading? The NICU day starts at 8 am (NICU_DAY_GRACE_HOUR), so before
  // 8 am "today" is still yesterday's sheet — the one the nurse is finishing.
  // Computed in the browser (India time); the server's /today uses its own
  // UTC clock and is off by 5:30 h. Refreshed when the baby or page changes
  // and after every DMS save.
  const [dmsToday, setDmsToday] = useState(null); // null | {state, label}
  useEffect(() => {
    if (!enrollmentId) { setDmsToday(null); return undefined; }
    let active = true;
    const load = () => {
      const now = new Date();
      const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (now.getHours() < NICU_DAY_GRACE_HOUR) day.setDate(day.getDate() - 1);
      const ymd = realCalendarDateYmd(day);
      const label = day.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      api.get(`/minimal-monitoring/${enrollmentId}/on/${ymd}`)
        .then(res => { if (active) setDmsToday({ state: sheetHasReading(res?.data) ? 'logged' : 'none', label }); })
        .catch(() => { if (active) setDmsToday(null); });
    };
    load();
    const onSaved = (e) => { if (!e?.detail?.enrollmentId || e.detail.enrollmentId === enrollmentId) load(); };
    window.addEventListener('portal-mml-saved', onSaved);
    return () => { active = false; window.removeEventListener('portal-mml-saved', onSaved); };
  }, [enrollmentId, location.pathname]);

  // Twins / triplets (PI 2026-10-29): every baby of this mother, so the
  // Form B link opens the right baby and a switcher can change baby.
  const [family, setFamily] = useState({ sid: null, total: 1, babies: [] });
  useEffect(() => {
    let active = true;
    const load = async () => {
      let sid = screeningId;
      if (!sid && enrollmentId) {
        try { sid = (await api.get(`/screenings/by-enrollment/${enrollmentId}`)).data?.screening_id || null; }
        catch (_) { sid = null; }
      }
      if (!sid) { if (active) setFamily({ sid: null, total: 1, babies: [] }); return; }
      try {
        const r = await api.get(`/screenings/${sid}/babies`);
        if (active) setFamily({ sid, total: r.data?.number_of_babies || 1, babies: r.data?.babies || [] });
      } catch (_) { if (active) setFamily({ sid, total: 1, babies: [] }); }
    };
    load();
    return () => { active = false; };
  }, [screeningId, enrollmentId, location.pathname]);
  const formBMatch = location.pathname.match(/^\/form-b\/[^/]+\/(\d+)/);
  const currentBaby = (family.babies.find(b => b.enrollment_id === enrollmentId) || {}).baby_number
    || (formBMatch ? Number(formBMatch[1]) : 1);
  const openBaby = (n) => {
    const b = family.babies.find(x => x.baby_number === n);
    if (b && b.enrollment_id) localStorage.setItem('current_enrollment_id', b.enrollment_id);
    else localStorage.removeItem('current_enrollment_id');
    if (family.sid) localStorage.setItem('current_screening_id', family.sid);
    window.dispatchEvent(new Event('storage'));
    const meta = SECTIONS.flatMap(sec => sec.items).find(i => i.id === currentForm);
    const stayOnForm = b && b.enrollment_id && meta && !['form_a', 'form_b'].includes(meta.id);
    guardedNavigate(null, stayOnForm ? `${meta.path}/${b.enrollment_id}`
      : (n === 1 ? `/form-b/${family.sid}` : `/form-b/${family.sid}/${n}`));
  };

  const [enrollmentLocked, setEnrollmentLocked] = useState(
    localStorage.getItem('enrollment_locked') === 'true'
  );
  useEffect(() => {
    const check = () => setEnrollmentLocked(localStorage.getItem('enrollment_locked') === 'true');
    window.addEventListener('storage', check);
    window.addEventListener('focus', check);
    return () => {
      window.removeEventListener('storage', check);
      window.removeEventListener('focus', check);
    };
  }, []);

  useEffect(() => {
    if (!screeningId && !enrollmentId) return;

    let active = true;
    const request = screeningId
      ? api.get(`/screenings/by-screening-id/${screeningId}`)
      : api.get(`/screenings/by-enrollment/${enrollmentId}`);

    request.then(res => {
      if (!active) return;
      const d = res.data || {};
      const consent = d.consent_given;
      const status = d.screening_status;
      const weeks = d.gestation_weeks;
      const days = d.gestation_days ?? 0;
      let gaOut = false;
      if (weeks != null && weeks !== "") {
        const t = Number(weeks) * 7 + Number(days || 0);
        gaOut = t < 25 * 7 || t > 31 * 7 + 6;
      } else if (d.gestation_known === "No" && d.ga_source === "Neither") {
        gaOut = true;
      }

      const shouldLock =
        status === "Screen Failure" ||
        gaOut ||
        !!d.exclusion_present ||
        (consent && !consentAllowsEnrollment(consent));

      if (shouldLock) {
        localStorage.setItem('enrollment_locked', 'true');
        if (gaOut || status === "Screen Failure") {
          localStorage.setItem(
            'enrollment_lock_reason',
            (d.gestation_known === "No" && d.ga_source === "Neither") ? 'ga_unknown' : 'ga_out_of_range'
          );
        } else if (d.exclusion_present) {
          localStorage.setItem('enrollment_lock_reason', 'exclusion');
        } else {
          localStorage.setItem('enrollment_lock_reason', 'consent');
        }
        setEnrollmentLocked(true);
        return;
      }

      /* no_ppv is per-enrollment. Confirm against THIS patient's birth row
         instead of keeping another baby's no_ppv lock forever. */
      const eid = pageEnrollmentId()
        || (d.enrollment_id && d.enrollment_id !== 'undefined' ? d.enrollment_id : null);
      const finishUnlock = () => {
        if (consentAllowsEnrollment(consent)) {
          localStorage.removeItem('enrollment_locked');
          localStorage.removeItem('enrollment_lock_reason');
          setEnrollmentLocked(false);
        }
      };
      if (eid && isUsableEnrollmentId(eid)) {
        api.get(`/enrollment-status/${eid}`).then(stRes => {
          if (!active) return;
          if (stRes.data?.no_ppv) {
            localStorage.setItem('enrollment_locked', 'true');
            localStorage.setItem('enrollment_lock_reason', 'no_ppv');
            setEnrollmentLocked(true);
          } else {
            finishUnlock();
          }
        }).catch(() => { if (active) finishUnlock(); });
        return;
      }

      finishUnlock();
    }).catch(() => {
      if (active) setEnrollmentLocked(localStorage.getItem('enrollment_locked') === 'true');
    });

    return () => { active = false; };
  }, [screeningId, enrollmentId]);

  const getPath = (form) => {
    const sid = ids.screeningId  && ids.screeningId  !== 'undefined' ? ids.screeningId  : null;
    const rawEid = ids.enrollmentId && ids.enrollmentId !== 'undefined' ? ids.enrollmentId : null;
    const eid = isUsableEnrollmentId(rawEid) ? String(rawEid).trim() : null;
    const motherSid = sid || family.sid;
    if (form.id === 'form_a') return motherSid ? `/form-a/${motherSid}` : '/form-a';
    // /form-b/:screeningId[/:babyNumber] — bare /form-b is handled by ScreeningFormRedirect.
    if (form.id === 'form_b') {
      if (!motherSid) return null;
      return currentBaby > 1 ? `/form-b/${motherSid}/${currentBaby}` : `/form-b/${motherSid}`;
    }
    // Routes are /form-c/:enrollmentId etc. — bare /form-c does not match any route (blank page).
    return eid ? `${form.path}/${eid}` : null;
  };

  const FORMS_ALLOWED_WHEN_NO_PPV = new Set(["form_a", "form_b"]);  // PI 2026-10-16: Form C only for randomised

  const getLockReason = () => localStorage.getItem("enrollment_lock_reason");

  const isUnlocked = (formId) => {
    /* Form A always stays open so GA / consent can be corrected */
    if (formId === "form_a") return true;

    const reason = getLockReason();
    /* PPV not required: only Forms A and B stay open */
    if (enrollmentLocked && reason === "no_ppv") {
      return FORMS_ALLOWED_WHEN_NO_PPV.has(formId);
    }
    if (enrollmentLocked) return false;
    return (PREREQS[formId] || []).every(isUnlockedPrereq);
  };

  const lockMessage = () => {
    const reason = getLockReason();
    if (reason === "ga_out_of_range") {
      return "Other forms locked — gestational age is outside the eligibility window (25w0d–31w6d).";
    }
    if (reason === "ga_unknown") {
      return "Other forms locked — gestational age cannot be determined.";
    }
    if (reason === "exclusion") {
      return "Other forms locked — exclusion criteria present.";
    }
    if (reason === "no_ppv") {
      return "Forms after Form B are locked — the baby did not need PPV. Only Forms A and B are filled.";
    }
    if (enrollmentLocked) {
      return "Enrollment locked — consent not given or participant not eligible.";
    }
    return null;
  };

  const countedDone = completedForms.filter(id => !NO_TICK_ITEMS.has(id)).length;
  const progressPct = TOTAL_FORMS > 0 ? Math.round((countedDone / TOTAL_FORMS) * 100) : 0;
  const currentMeta = getCurrentFormMeta(currentForm);

  return (
    <aside className={`sidebar${navOpen ? ' is-drawer-open' : ''}`}>

      {/* Narrow screens: one sticky chrome bar */}
      <div className="sidebar-sticky-bar">
        <button
          type="button"
          className="sidebar-menu-btn"
          onClick={toggleNav}
          aria-expanded={navOpen}
          aria-controls="sidebar-nav"
          title={navOpen ? 'Close forms menu' : 'Open forms menu'}
        >
          {navOpen ? <X size={18} strokeWidth={2.5} /> : <Menu size={18} strokeWidth={2.5} />}
        </button>

        <button
          type="button"
          className="sidebar-sticky-brand"
          onClick={() => { closeNav(); navigate('/dashboard'); }}
          title="Dashboard"
        >
          <img src="/logo.png" alt="" className="sidebar-sticky-logo" />
        </button>

        <div className="sidebar-sticky-current">
          <span className="sidebar-sticky-label">
            {currentMeta ? currentMeta.label : 'PORTAL'}
          </span>
          <span className="sidebar-sticky-sub">
            {currentMeta ? currentMeta.sub : 'Clinical EDC'}
          </span>
        </div>

        <span className="sidebar-sticky-progress" title="Case progress">
          {countedDone}/{TOTAL_FORMS}
        </span>

        <div className="sidebar-sticky-user">
          <div className="sidebar-avatar" title={user?.name || 'User'}>
            {(user?.name || 'U')[0].toUpperCase()}
          </div>
          <button
            type="button"
            className="sidebar-logout"
            onClick={() => { localStorage.clear(); window.location.href = '/login'; }}
            title="Log out"
          >
            <LogOut size={14} strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Desktop header */}
      <div className="sidebar-header">
        <div className="sidebar-brand" onClick={() => guardedNavigate(null, '/dashboard')} title="Dashboard">
          <div className="sidebar-logo-box">
            <img src="/logo.png" alt="PORTAL" className="sidebar-logo-img" />
          </div>
          <div>
            <div className="sidebar-header-title">PORTAL Trial</div>
            <div className="sidebar-header-sub">Clinical EDC · CRF 1.26</div>
          </div>
        </div>
        <div className="sidebar-progress">
          <div className="progress-label">
            <span>Case Progress</span>
            <span>{countedDone} / {TOTAL_FORMS} forms</span>
          </div>
          <div className="progress-bar-bg">
            <div className="progress-bar-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      </div>

      <button
        type="button"
        className="sidebar-drawer-backdrop"
        aria-label="Close forms menu"
        tabIndex={navOpen ? 0 : -1}
        onClick={closeNav}
      />

      <nav id="sidebar-nav" className={`sidebar-nav${navOpen ? ' nav-open' : ''}`}>
        <div className="sidebar-drawer-head">
          <div>
            <div className="sidebar-drawer-title">Forms</div>
            <div className="sidebar-drawer-sub">PORTAL Trial · CRF v1.26</div>
          </div>
          <button type="button" className="sidebar-drawer-close" onClick={closeNav} aria-label="Close">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>
        <div className="sidebar-nav-inner">
          <NavLink to="/dashboard"
            onClick={(e) => guardedNavigate(e, '/dashboard')}
            className={({ isActive }) => `sidebar-dash-link${isActive ? ' active' : ''}`}>
            <LayoutDashboard size={14} strokeWidth={2} />
            <span>Dashboard</span>
          </NavLink>
          <NavLink to="/trial-monitoring"
            onClick={(e) => guardedNavigate(e, '/trial-monitoring')}
            className={({ isActive }) => `sidebar-dash-link${isActive ? ' active' : ''}`}>
            <BarChart3 size={14} strokeWidth={2} />
            <span>Trial Monitoring</span>
          </NavLink>
          <NavLink to="/helper-form-records"
            onClick={(e) => guardedNavigate(e, '/helper-form-records')}
            className={({ isActive }) => `sidebar-dash-link${isActive ? ' active' : ''}`}>
            <ClipboardList size={14} strokeWidth={2} />
            <span>Helper Form Records</span>
          </NavLink>
          <NavLink to="/ga-check"
            onClick={(e) => guardedNavigate(e, '/ga-check')}
            className={({ isActive }) => `sidebar-dash-link${isActive ? ' active' : ''}`}>
            <Search size={14} strokeWidth={2} />
            <span>Gestation (Inclusion Criteria) Log</span>
          </NavLink>
          <NavLink to="/log-of-all-births"
            onClick={(e) => guardedNavigate(e, '/log-of-all-births')}
            className={({ isActive }) => `sidebar-dash-link${isActive ? ' active' : ''}`}>
            <Hospital size={14} strokeWidth={2} />
            <span>Log of All Births</span>
          </NavLink>
          <div className="sidebar-sep" />

          {family.total > 1 && (
            <div className="sidebar-babies" aria-label="Babies of this mother">
              <span className="sidebar-babies-title">
                {({ 2: 'Twins', 3: 'Triplets', 4: 'Quadruplets' })[family.total] || 'Babies'} · mother {family.sid}
              </span>
              <div className="sidebar-babies-row">
                {Array.from({ length: family.total }, (_, i) => i + 1).map(n => {
                  const b = family.babies.find(x => x.baby_number === n);
                  const label = !b ? 'no Form B' : String(b.enrollment_id).startsWith('NR-') ? 'not randomised' : b.enrollment_id;
                  return (
                    <button key={n} type="button"
                      className={`sidebar-baby-btn${n === currentBaby ? ' is-current' : ''}${b ? '' : ' is-missing'}`}
                      onClick={() => openBaby(n)} title={`Baby ${n}: ${label}`}>
                      Baby {n}<small>{label}</small>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!isProgressLoaded ? (
            <div className="sidebar-loading">
              <div className="sb-dots"><span/><span/><span/></div>
              <span>Loading…</span>
            </div>
          ) : SECTIONS.map(section => {
            const counted = section.items.filter(i => !NO_TICK_ITEMS.has(i.id));
            const done  = counted.filter(i => completedForms.includes(i.id)).length;
            const total = counted.length;
            return (
              <div key={section.key} className="sidebar-section">
                <div className="sidebar-section-header">
                  <span className="sidebar-section-title">{section.title}</span>
                  <span className={`sidebar-section-badge ${done === total ? 'all-done' : ''}`}>
                    {done}/{total}
                  </span>
                </div>

                {section.items.map(form => {
                  const completed = !NO_TICK_ITEMS.has(form.id) && completedForms.includes(form.id);
                  const unlocked  = isUnlocked(form.id);
                  const locked    = !unlocked;
                  const isCurrent = currentForm === form.id;
                  const { Icon }  = form;
                  const path      = getPath(form);
                  const needsScreening = form.id === 'form_b';
                  const needsEnrollment = form.id !== 'form_a' && form.id !== 'form_b';
                  const missingScreening = needsScreening && !path;
                  const missingEnrollment = needsEnrollment && !path;
                  const missingPatientId = missingScreening || missingEnrollment;
                  const stateClass = locked ? 'state-locked' : completed ? 'state-done' : isCurrent ? 'state-active' : 'state-open';

                  return (
                    <NavLink key={form.id} to={locked || missingPatientId ? '#' : path}
                      onClick={e => {
                        if (missingScreening) {
                          e.preventDefault();
                          alert(
                            'Screening ID is not set. Complete Form A or open the participant ' +
                            'from View Entries, then open Form B.',
                          );
                          return;
                        }
                        if (missingEnrollment) {
                          e.preventDefault();
                          alert(
                            'Enrollment ID is not set for this patient yet. Complete Form B ' +
                            '(save with a valid enrollment ID or NR- placeholder) or open the ' +
                            'patient from View Entries.',
                          );
                          return;
                        }
                        if (locked) {
                          e.preventDefault();
                          const msg = lockMessage();
                          if (msg) {
                            alert(msg);
                            return;
                          }
                          const missing = (PREREQS[form.id] || [])
                            .filter(p => !isUnlockedPrereq(p))
                            .map(p => p === 'form_a' ? 'Form A (Screening)' : 'Form B (Birth & Resuscitation)')
                            .join(' and ');
                          alert(`Complete ${missing || 'Form A and Form B'} first to unlock all forms.`);
                          return;
                        }
                        guardedNavigate(e, path);
                      }}
                      className={({ isActive }) =>
                        `sidebar-item ${stateClass}${isActive && !locked ? ' nav-active' : ''}`
                      }
                    >
                      {isCurrent && !locked && <div className="item-accent" />}
                      <div className={`item-icon ${stateClass}`}>
                        {locked    ? <Lock  size={12} strokeWidth={2.5} /> :
                         completed ? <Check size={12} strokeWidth={3}   /> :
                                     <Icon  size={12} strokeWidth={2}   />}
                      </div>
                      <div className="item-text">
                        <span className="item-label">{form.label}</span>
                        <span className="item-sub">
                          {form.id === 'minimal_monitoring' && enrollmentId && dmsToday
                            ? `Today (${dmsToday.label}): ${dmsToday.state === 'logged' ? 'logged' : 'not yet logged'}`
                            : form.sub}
                        </span>
                      </div>
                      <div className="item-right">
                        {completed ? (
                          <span className="item-badge done">✓</span>
                        ) : locked ? (
                          <Lock size={10} strokeWidth={2.5} className="item-lock-icon" />
                        ) : isCurrent ? (
                          <span className="item-badge active">Active</span>
                        ) : (
                          <ChevronRight size={12} className="item-chevron" />
                        )}
                      </div>
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </div>
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-avatar">{(user?.name || 'U')[0].toUpperCase()}</div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{user?.name || 'User'}</span>
            <span className="sidebar-user-role">
              {ROLE_LABELS[user?.role] || user?.role || 'Staff'}
              {user?.site ? ` · ${user.site}` : ''}
            </span>
          </div>
          <button className="sidebar-logout" onClick={() => { localStorage.clear(); window.location.href = '/login'; }} title="Log out">
            <LogOut size={14} strokeWidth={2} />
          </button>
        </div>
      </div>

    </aside>
  );
}
